import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Polyline, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { PlannedStop, SalesViewMapPoint } from '../../api/types';
import { siftApiClient } from '../../api/siftApiClient';
import { buildRoute, groupStopsByLocation, tripColor } from './salesPlanFormat';
import type { PlanRoute } from './salesPlanFormat';
import { TIER_COLOR, TIER_ORDER, tierFromIndex } from './salesViewTokens';
import type { SalesOutFeature, SalesLeafFeature } from './salesClusterWorker';
import { spiderfyPositions } from '../common/spiderfyLayout';
import { formatHealthPercent } from './salesViewFormat';
import 'leaflet/dist/leaflet.css';

const LS_LEGEND_HIDDEN = 'sift:sales-view:mapLegendHidden';

// Same ~66m floor Client View uses: clients in one ZIP share a centroid, so
// without this they stack into a single un-clickable dot.
const MIN_SEP_DEG = 0.0006;
const SPIDERFY_MAX = 50;

const fanOutLeaves = (
  leaves: { clientId: string; lat: number; lng: number }[],
): Map<string, [number, number]> => {
  const groups = new Map<string, { clientId: string; lat: number; lng: number }[]>();
  for (const l of leaves) {
    const key = `${l.lat.toFixed(5)},${l.lng.toFixed(5)}`;
    const g = groups.get(key);
    if (g) g.push(l);
    else groups.set(key, [l]);
  }
  const plot = new Map<string, [number, number]>();
  for (const g of groups.values()) {
    if (g.length === 1) {
      plot.set(g[0].clientId, [g[0].lat, g[0].lng]);
      continue;
    }
    const ringR = MIN_SEP_DEG / (2 * Math.sin(Math.PI / g.length));
    g.forEach((l, i) => {
      const angle = (2 * Math.PI * i) / g.length;
      const offLat = l.lat + ringR * Math.sin(angle);
      const offLng = l.lng + (ringR * Math.cos(angle)) / Math.cos((l.lat * Math.PI) / 180);
      plot.set(l.clientId, [offLat, offLng]);
    });
  }
  return plot;
};

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string
  ));

/** Popup from the THIN payload only. Anything richer belongs in the drawer --
 *  the map payload deliberately has no metrics on it. */
const buildPopupHtml = (p: SalesViewMapPoint): string => {
  const tier = TIER_COLOR[p.tier] ?? TIER_COLOR.LOW;
  const name = escapeHtml(p.client_name || `Client ${p.client_id}`);
  const darkLine = p.went_dark ? '<div class="sv-popup-dark">Went dark</div>' : '';
  return `
    <div class="sv-popup">
      <div class="sv-popup-header">
        <div class="sv-popup-name">${name}</div>
        <span class="sv-popup-tier" style="background:${tier.bg};color:${tier.fg}">${p.tier}</span>
      </div>
      <div class="sv-popup-id">${escapeHtml(p.client_id)} · health ${formatHealthPercent(p.health_score)}</div>
      ${darkLine}
    </div>
  `;
};

interface SalesViewMapProps {
  points: SalesViewMapPoint[];
  loading: boolean;
  ungeocodedCount: number;
  selectedClientId?: string | null;
  onClientClick?: (clientId: string | null) => void;
  onClientHover?: (clientId: string | null) => void;
  /** VISIT stops from the open plan, drawn as one line per trip. Empty or
   *  omitted leaves the map exactly as it was. */
  planStops?: PlannedStop[];
}

const MapBoundsUpdater: React.FC<{ points: SalesViewMapPoint[] }> = ({ points }) => {
  const map = useMap();
  const prevBoundsKey = useRef('');

  useEffect(() => {
    const boundsKey = points.map((p) => p.client_id).join(',');
    if (boundsKey === prevBoundsKey.current) return;
    prevBoundsKey.current = boundsKey;

    const coords = points
      .filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => [p.latitude, p.longitude] as [number, number]);
    if (coords.length > 0) map.fitBounds(L.latLngBounds(coords), { padding: [40, 40] });
    else map.setView([39.8283, -98.5795], 4);
  }, [points, map]);

  return null;
};

const MapResizeObserver: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(() => map.invalidateSize());
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [map]);
  return null;
};


/**
 * Client layer: clustered via the worker, drawn on one shared canvas renderer.
 *
 * Pin fill is tier. The aged-listing halo that used to be the second scale was
 * removed with the aged signal (§15 delta D12) -- it counted VINs that were not
 * on a Manheim listing at all.
 */
const SalesClusterLayer: React.FC<{
  points: SalesViewMapPoint[];
  selectedClientId?: string | null;
  onClientClick?: (clientId: string | null) => void;
  onClientHover?: (clientId: string | null) => void;
}> = ({ points, selectedClientId, onClientClick, onClientHover }) => {
  const map = useMap();
  const workerRef = useRef<Worker | null>(null);
  const rendererRef = useRef<L.Canvas | null>(null);
  const clusterLayerRef = useRef<L.LayerGroup | null>(null);
  const pointLayerRef = useRef<L.LayerGroup | null>(null);
  const spiderLayerRef = useRef<L.LayerGroup | null>(null);
  const spiderClusterRef = useRef<number | null>(null);
  const pointByIdRef = useRef<Map<string, SalesViewMapPoint>>(new Map());
  const reqIdRef = useRef(0);
  const loadedRef = useRef(false);
  const pendingOpenRef = useRef<string | null>(null);

  const onClickRef = useRef(onClientClick);
  onClickRef.current = onClientClick;
  const onHoverRef = useRef(onClientHover);
  onHoverRef.current = onClientHover;
  const selectedRef = useRef<string | null | undefined>(selectedClientId);
  selectedRef.current = selectedClientId;

  const makeLeafCircle = useCallback((f: SalesLeafFeature, lat: number, lng: number): L.CircleMarker => {
    const fill = TIER_COLOR[tierFromIndex(f.tier)].pin;
    const isSelected = f.clientId === selectedRef.current;
    const circle = L.circleMarker([lat, lng], {
      renderer: rendererRef.current ?? undefined,
      radius: isSelected ? 9 : 6,
      fillColor: fill,
      fillOpacity: 1,
      color: '#ffffff',
      weight: isSelected ? 4 : 1.5,
    });
    const point = pointByIdRef.current.get(f.clientId);
    if (point) {
      circle.bindPopup(() => buildPopupHtml(point), {
        maxWidth: 280, autoPan: false, closeButton: false,
      });
    }
    circle.on('mouseover', () => { circle.openPopup(); onHoverRef.current?.(f.clientId); });
    circle.on('mouseout', () => { circle.closePopup(); onHoverRef.current?.(null); });
    circle.on('click', (ev) => {
      L.DomEvent.stopPropagation(ev);
      onClickRef.current?.(f.clientId);
    });
    return circle;
  }, []);

  const clearSpider = useCallback(() => {
    spiderLayerRef.current?.clearLayers();
    spiderClusterRef.current = null;
  }, []);

  const spiderfy = useCallback((clusterId: number, lat: number, lng: number, leaves: SalesLeafFeature[]) => {
    const layer = spiderLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const centre = L.latLng(lat, lng);
    const positions = spiderfyPositions(map, centre, leaves.length);
    leaves.forEach((leaf, i) => {
      const pos = positions[i];
      layer.addLayer(L.polyline([centre, pos], {
        color: '#94a3b8', weight: 1.5, opacity: 0.7, interactive: false,
      }));
      layer.addLayer(makeLeafCircle(leaf, pos.lat, pos.lng));
    });
    spiderClusterRef.current = clusterId;
  }, [map, makeLeafCircle]);

  const render = useCallback((features: SalesOutFeature[]) => {
    const clusterLayer = clusterLayerRef.current;
    const pointLayer = pointLayerRef.current;
    if (!clusterLayer || !pointLayer || !rendererRef.current) return;

    clusterLayer.clearLayers();
    pointLayer.clearLayers();

    const leaves = features.filter((f): f is SalesLeafFeature => !f.cluster);
    const plot = fanOutLeaves(leaves.map((l) => ({ clientId: l.clientId, lat: l.lat, lng: l.lng })));

    for (const f of features) {
      if (f.cluster) {
        const bg = TIER_COLOR[tierFromIndex(f.minTier)].pin;
        const size = f.count >= 50 ? 'large' : f.count >= 10 ? 'medium' : 'small';
        const icon = L.divIcon({
          html: `<div class="sv-cluster sv-cluster-${size}" style="background:${bg}"><span>${f.count}</span></div>`,
          className: 'sv-cluster-wrapper',
          iconSize: L.point(36, 36),
        });
        const marker = L.marker([f.lat, f.lng], { icon });
        marker.on('click', (ev) => {
          L.DomEvent.stopPropagation(ev);
          if (spiderClusterRef.current === f.id) { clearSpider(); return; }
          clearSpider();
          workerRef.current?.postMessage({
            type: 'expand', clusterId: f.id, lat: f.lat, lng: f.lng, limit: SPIDERFY_MAX,
          });
        });
        clusterLayer.addLayer(marker);
      } else {
        const [lat, lng] = plot.get(f.clientId) ?? [f.lat, f.lng];
        const circle = makeLeafCircle(f, lat, lng);
        pointLayer.addLayer(circle);
        if (pendingOpenRef.current === f.clientId) {
          circle.openPopup();
          pendingOpenRef.current = null;
        }
      }
    }
  }, [makeLeafCircle, clearSpider]);

  const query = useCallback(() => {
    if (!loadedRef.current || !workerRef.current) return;
    const b = map.getBounds();
    const bbox: [number, number, number, number] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    const id = ++reqIdRef.current;
    workerRef.current.postMessage({ type: 'query', id, bbox, zoom: Math.round(map.getZoom()) });
  }, [map]);

  useEffect(() => {
    rendererRef.current = L.canvas({ padding: 0.5 });
    const clusterLayer = L.layerGroup().addTo(map);
    const pointLayer = L.layerGroup().addTo(map);
    const spiderLayer = L.layerGroup().addTo(map);
    clusterLayerRef.current = clusterLayer;
    pointLayerRef.current = pointLayer;
    spiderLayerRef.current = spiderLayer;

    const worker = new Worker(new URL('./salesClusterWorker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;
    worker.onmessage = (event: MessageEvent) => {
      const msg = event.data;
      if (msg.type === 'loaded') {
        loadedRef.current = true;
        query();
      } else if (msg.type === 'result') {
        if (msg.id !== reqIdRef.current) return;
        render(msg.features as SalesOutFeature[]);
      } else if (msg.type === 'expanded') {
        if (msg.leaves.length > 0) {
          spiderfy(msg.clusterId, msg.lat, msg.lng, msg.leaves as SalesLeafFeature[]);
        } else {
          map.setView([msg.lat, msg.lng], Math.min(msg.expansionZoom, map.getMaxZoom()));
        }
      }
    };

    const handleBackgroundClick = () => {
      clearSpider();
      onClickRef.current?.(null);
    };
    map.on('moveend', query);
    map.on('movestart', clearSpider);
    map.on('click', handleBackgroundClick);

    return () => {
      map.off('moveend', query);
      map.off('movestart', clearSpider);
      map.off('click', handleBackgroundClick);
      worker.terminate();
      workerRef.current = null;
      map.removeLayer(clusterLayer);
      map.removeLayer(pointLayer);
      map.removeLayer(spiderLayer);
      clusterLayerRef.current = null;
      pointLayerRef.current = null;
      spiderLayerRef.current = null;
    };
  }, [map, query, render, spiderfy, clearSpider]);

  useEffect(() => {
    pointByIdRef.current = new Map(points.map((p) => [p.client_id, p]));
    loadedRef.current = false;
    const payload = points
      .filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => ({
        id: p.client_id,
        lng: p.longitude,
        lat: p.latitude,
        tier: Math.max(0, TIER_ORDER.indexOf(p.tier)),
        aged: 0,
      }));
    workerRef.current?.postMessage({ type: 'load', points: payload });
  }, [points]);


  useEffect(() => {
    pendingOpenRef.current = selectedClientId ?? null;
    if (selectedClientId) {
      const point = pointByIdRef.current.get(selectedClientId);
      if (point && point.latitude != null && point.longitude != null) {
        const zoom = map.getZoom();
        map.setView([point.latitude, point.longitude], zoom < 14 ? 14 : zoom);
      }
    }
    query();
  }, [selectedClientId, map, query]);

  return null;
};

/** Compass bearing a→b, in screen degrees (0 = up), for rotating an arrow head. */
const bearing = (a: [number, number], b: [number, number]): number => {
  const toRad = Math.PI / 180;
  const dLon = (b[1] - a[1]) * toRad;
  const y = Math.sin(dLon) * Math.cos(b[0] * toRad);
  const x = Math.cos(a[0] * toRad) * Math.sin(b[0] * toRad)
    - Math.sin(a[0] * toRad) * Math.cos(b[0] * toRad) * Math.cos(dLon);
  return (Math.atan2(y, x) * 180) / Math.PI;
};

/** The plan route, and NOTHING else.
 *
 *  In plan mode the clustered client layer is replaced rather than layered under: a rep
 *  looking at their week does not want the other 600 clients in the territory competing
 *  with the eight they are driving to, and cluster bubbles at low zoom hid the route.
 */
const PlanRouteLayer: React.FC<{
  route: PlanRoute;
  stops: PlannedStop[];
  coords: Map<string, [number, number]>;
  roadFor: (positions: Array<[number, number]>) => Array<[number, number]> | undefined;
  onClientClick?: (clientId: string | null) => void;
  onClientHover?: (clientId: string | null) => void;
}> = ({ route, stops, coords, roadFor, onClientClick, onClientHover }) => {
  const map = useMap();

  useEffect(() => {
    const layer = L.layerGroup().addTo(map);
    const spider = L.layerGroup().addTo(map);
    let openKey: string | null = null;

    const clearSpider = () => { spider.clearLayers(); openKey = null; };

    const stopPin = (stop: PlannedStop, at: [number, number]): L.Marker => {
      const isCall = stop.contact_mode === 'CALL';
      const colour = isCall || stop.trip_no == null
        ? 'var(--color-text-secondary, #6b7280)'
        : tripColor(stop.trip_no);
      // Trip and stop, e.g. "2.3". A call has neither, so it gets a phone glyph -- numbering
      // it would put it in a drive it is not part of.
      const label = isCall || stop.trip_no == null ? '☎' : `${stop.trip_no}.${stop.stop_no}`;
      const marker = L.marker(at, {
        icon: L.divIcon({
          className: 'sv-route-pin',
          iconSize: [26, 26],
          iconAnchor: [13, 13],
          html: `<span style="background:${colour}">${label}</span>`,
        }),
        title: stop.client_name ?? stop.client_id,
      });
      marker.on('click', (ev) => {
        L.DomEvent.stopPropagation(ev);
        onClientClick?.(stop.client_id);
      });
      marker.on('mouseover', () => onClientHover?.(stop.client_id));
      marker.on('mouseout', () => onClientHover?.(null));
      return marker;
    };

    const groups = groupStopsByLocation(stops, coords);

    for (const line of route.lines) {
      const road = roadFor(line.positions);
      // One arrow per LEG, pointing the way the rep drives; on the road when we have it.
      for (let i = 1; i < line.positions.length; i += 1) {
        let from = line.positions[i - 1];
        let to = line.positions[i];
        let mid: [number, number] = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
        if (road && road.length > 1) {
          let best = 0;
          let bestD = Infinity;
          road.forEach((p, k) => {
            const d = (p[0] - mid[0]) ** 2 + (p[1] - mid[1]) ** 2;
            if (d < bestD) { bestD = d; best = k; }
          });
          const k = Math.min(best, road.length - 2);
          [from, to] = [road[k], road[k + 1]];
          mid = road[k];
        }
        L.marker(mid, {
          interactive: false,
          icon: L.divIcon({
            className: 'sv-route-arrow',
            iconSize: [14, 14],
            iconAnchor: [7, 7],
            html: `<span style="transform:rotate(${bearing(from, to)}deg);color:${line.color}">▲</span>`,
          }),
        }).addTo(layer);
      }
    }

    for (const group of groups) {
      const key = group.key;
      if (group.stops.length === 1) {
        stopPin(group.stops[0], group.point).addTo(layer);
        continue;
      }
      // Every stop here shares a ZIP centroid. One bubble with the count, and the members
      // only on demand -- stacked pins hid all but the last, and the rep could not tell.
      const trips = new Set(group.stops.map((s) => s.trip_no));
      const colour = trips.size === 1 && group.stops[0].trip_no != null
        ? tripColor(group.stops[0].trip_no)
        : 'var(--color-text-secondary, #6b7280)';
      const size = group.stops.length < 5 ? 'small' : group.stops.length < 10 ? 'medium' : 'large';
      const bubble = L.marker(group.point, {
        icon: L.divIcon({
          className: 'sv-cluster-wrapper',
          html: `<div class="sv-cluster sv-cluster-${size}" style="background:${colour}">`
            + `<span>${group.stops.length}</span></div>`,
          iconSize: L.point(36, 36),
        }),
        title: `${group.stops.length} stops at this ZIP — click to fan out`,
      }).addTo(layer);
      bubble.on('click', (ev) => {
        L.DomEvent.stopPropagation(ev);
        if (openKey === key) { clearSpider(); return; }
        clearSpider();
        const centre = L.latLng(group.point[0], group.point[1]);
        const positions = spiderfyPositions(map, centre, group.stops.length);
        group.stops.forEach((s, i) => {
          const pos = positions[i];
          spider.addLayer(L.polyline([centre, pos], {
            color: '#94a3b8', weight: 1.5, opacity: 0.7, interactive: false,
          }));
          spider.addLayer(stopPin(s, [pos.lat, pos.lng]));
        });
        openKey = key;
      });
    }

    // Same convention as the cluster map: the legs are laid out in screen space at the
    // current zoom, so a move invalidates them rather than dragging them out of shape.
    map.on('movestart', clearSpider);
    map.on('click', clearSpider);

    return () => {
      map.off('movestart', clearSpider);
      map.off('click', clearSpider);
      layer.remove();
      spider.remove();
    };
  }, [map, route, stops, coords, roadFor, onClientClick, onClientHover]);

  return (
    <>
      {route.lines.map((line) => {
        const road = roadFor(line.positions);
        return (
          <Polyline
            key={line.tripNo}
            positions={road ?? line.positions}
            pathOptions={{
              color: line.color, weight: road ? 4 : 3, opacity: 0.85,
              dashArray: road ? undefined : '6 6',
            }}
          />
        );
      })}
    </>
  );
};

export const SalesViewMap: React.FC<SalesViewMapProps> = React.memo(({
  points,
  loading,
  ungeocodedCount,
  selectedClientId,
  onClientClick,
  onClientHover,
  planStops,
}) => {
  const route = useMemo(
    () => (planStops?.length ? buildRoute(planStops, points) : null),
    [planStops, points],
  );
  const planMode = Boolean(route && planStops?.length);
  /** Trips switched off in the legend. Empty = every trip shown, the default. */
  const [hiddenTrips, setHiddenTrips] = useState<Set<number>>(() => new Set());
  const [callsHidden, setCallsHidden] = useState(false);
  /** Every trip in the plan, including one-stop trips that draw no line. */
  const trips = useMemo(() => {
    const counts = new Map<number, number>();
    for (const s of planStops ?? []) {
      if (s.contact_mode !== 'CALL' && s.trip_no != null) {
        counts.set(s.trip_no, (counts.get(s.trip_no) ?? 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => a[0] - b[0]);
  }, [planStops]);
  const callCount = useMemo(
    () => (planStops ?? []).filter((s) => s.contact_mode === 'CALL' || s.trip_no == null).length,
    [planStops],
  );
  const tripKey = trips.map(([t]) => t).join(',');
  // A different week, or rebuilt trips, starts with everything visible again.
  useEffect(() => { setHiddenTrips(new Set()); setCallsHidden(false); }, [tripKey]);
  const toggleTrip = useCallback((tripNo: number) => {
    setHiddenTrips((prev) => {
      const next = new Set(prev);
      if (next.has(tripNo)) next.delete(tripNo);
      else next.add(tripNo);
      return next;
    });
  }, []);
  const showOnly = useCallback((tripNo: number) => {
    setHiddenTrips(new Set(trips.map(([t]) => t).filter((t) => t !== tripNo)));
    setCallsHidden(true);
  }, [trips]);
  const showAll = useCallback(() => {
    setHiddenTrips(new Set());
    setCallsHidden(false);
  }, []);
  const visibleStops = useMemo(
    () => (planStops ?? []).filter((s) => (
      s.contact_mode === 'CALL' || s.trip_no == null ? !callsHidden : !hiddenTrips.has(s.trip_no)
    )),
    [planStops, hiddenTrips, callsHidden],
  );
  const visibleRoute = useMemo(
    () => (route && hiddenTrips.size
      ? { ...route, lines: route.lines.filter((l) => !hiddenTrips.has(l.tripNo)) }
      : route),
    [route, hiddenTrips],
  );
  /** Only the clients in the plan, so the map fits the week rather than the territory. */
  const routePoints = useMemo(() => {
    if (!planStops?.length) return points;
    const ids = new Set((visibleStops.length ? visibleStops : planStops).map((s) => s.client_id));
    return points.filter((p) => ids.has(p.client_id));
  }, [planStops, visibleStops, points]);
  const routeCoords = useMemo(() => {
    const map = new Map<string, [number, number]>();
    for (const p of routePoints) {
      if (p.latitude != null && p.longitude != null) map.set(p.client_id, [p.latitude, p.longitude]);
    }
    return map;
  }, [routePoints]);
  /** Road geometry per trip, keyed by its ordered stop points so a re-order refetches. */
  const [shapes, setShapes] = useState<Map<string, Array<[number, number]>>>(() => new Map());
  const shapeKey = (positions: Array<[number, number]>) => positions.map((p) => p.join(',')).join('|');
  useEffect(() => {
    const wanted = (route?.lines ?? []).filter((l) => !shapes.has(shapeKey(l.positions)));
    if (!wanted.length) return;
    let cancelled = false;
    siftApiClient.getSalesPlanRouteShapes(
      wanted.map((l) => ({ trip_no: l.tripNo, points: l.positions })),
    ).then((res) => {
      if (cancelled) return;
      setShapes((prev) => {
        const next = new Map(prev);
        for (const t of res.trips) {
          const line = wanted.find((l) => l.tripNo === t.trip_no);
          if (line && t.line?.length) next.set(shapeKey(line.positions), t.line);
        }
        return next;
      });
    }).catch(() => { /* straight lines stand in, and the legend says so */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);
  const roadFor = useCallback(
    (positions: Array<[number, number]>) => shapes.get(shapeKey(positions)),
    [shapes],
  );
  const anyStraight = (visibleRoute?.lines ?? []).some((l) => !roadFor(l.positions));
  const [legendHidden, setLegendHidden] = useState<boolean>(
    () => localStorage.getItem(LS_LEGEND_HIDDEN) === 'true',
  );
  const toggleLegend = useCallback(() => {
    setLegendHidden((prev) => {
      const next = !prev;
      localStorage.setItem(LS_LEGEND_HIDDEN, String(next));
      return next;
    });
  }, []);

  return (
    <div className="sv-map-wrapper">
      {loading && <div className="sv-map-loading"><span>Loading clients…</span></div>}

      <MapContainer center={[39.8283, -98.5795]} zoom={4} className="sv-map" scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapBoundsUpdater points={planMode ? routePoints : points} />
        <MapResizeObserver />
        {planMode ? (
          <PlanRouteLayer
            route={visibleRoute!}
            stops={visibleStops}
            coords={routeCoords}
            roadFor={roadFor}
            onClientClick={onClientClick}
            onClientHover={onClientHover}
          />
        ) : (
          <SalesClusterLayer
            points={points}
            selectedClientId={selectedClientId}
            onClientClick={onClientClick}
            onClientHover={onClientHover}
          />
        )}
      </MapContainer>

      <div className={`sv-map-legend ${legendHidden ? 'collapsed' : ''}`}>
        <button type="button" className="sv-map-legend-toggle" onClick={toggleLegend}>
          <span>Legend</span>
          <span className="sv-map-legend-caret">{legendHidden ? '▸' : '▾'}</span>
        </button>

        {!legendHidden && (
          <div className="sv-map-legend-body">
            <div className="sv-map-legend-section">
              <div className="sv-map-legend-title">Tier (fill)</div>
              {TIER_ORDER.map((t) => (
                <div key={t} className="sv-map-legend-row">
                  <span className="sv-map-legend-dot" style={{ background: TIER_COLOR[t].pin }} />
                  <span>{t}</span>
                </div>
              ))}
            </div>


            {route && (
              <div className="sv-map-legend-section">
                <div className="sv-map-legend-title">
                  Plan route
                  {(hiddenTrips.size > 0 || callsHidden) && (
                    <button type="button" className="sv-map-legend-link" onClick={showAll}>
                      Show all
                    </button>
                  )}
                </div>
                {trips.map(([tripNo, count]) => (
                  <div key={tripNo} className="sv-map-legend-row sv-map-legend-trip">
                    <label>
                      <input
                        type="checkbox"
                        checked={!hiddenTrips.has(tripNo)}
                        onChange={() => toggleTrip(tripNo)}
                        aria-label={`Show trip ${tripNo} on the map`}
                      />
                      <span className="sv-map-legend-line" style={{ background: tripColor(tripNo) }} />
                      <span>Trip {tripNo} · {count} stop{count === 1 ? '' : 's'}</span>
                    </label>
                    {trips.length + (callCount ? 1 : 0) > 1 && (
                      <button
                        type="button"
                        className="sv-map-legend-link"
                        onClick={() => showOnly(tripNo)}
                        aria-label={`Show only trip ${tripNo}`}
                      >
                        only
                      </button>
                    )}
                  </div>
                ))}
                {callCount > 0 && (
                  <div className="sv-map-legend-row sv-map-legend-trip">
                    <label>
                      <input
                        type="checkbox"
                        checked={!callsHidden}
                        onChange={() => setCallsHidden((h) => !h)}
                        aria-label="Show calls on the map"
                      />
                      <span className="sv-map-legend-call">☎</span>
                      <span>Calls · {callCount}</span>
                    </label>
                  </div>
                )}
                {anyStraight && (
                  <div className="sv-map-legend-note">
                    Dashed lines are straight from stop to stop: the road route was unavailable.
                  </div>
                )}
                {route.unplottable.length > 0 && (
                  <div className="sv-map-legend-note">
                    {route.unplottable.length} planned stop
                    {route.unplottable.length === 1 ? ' is' : 's are'} not on the map — they are
                    still in the plan list.
                  </div>
                )}
                {route.hasGaps && (
                  <div className="sv-map-legend-note">
                    A line runs straight past a stop it could not place, so the route drawn is
                    shorter than the drive.
                  </div>
                )}
              </div>
            )}

            {ungeocodedCount > 0 && (
              <div className="sv-map-legend-note">
                {ungeocodedCount.toLocaleString()} client{ungeocodedCount === 1 ? '' : 's'} not
                mapped (no ZIP centroid)
              </div>
            )}
            <div className="sv-map-legend-note">
              Pins sit at the dealership address, or the ZIP centre where we have no address.
              Stops at one spot fan out on click.
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

SalesViewMap.displayName = 'SalesViewMap';

export default SalesViewMap;
